/**
 * oddit-section-collection-carousel
 * Horizontal scrolling carousel with arrow navigation, autoplay, and swipe support.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-collection-carousel';
  var TRACK_SELECTOR = '.oddit-section-collection-carousel__track';
  var CARD_SELECTOR = '.oddit-section-collection-carousel__card';
  var PREV_SELECTOR = '.oddit-section-collection-carousel__arrow--prev';
  var NEXT_SELECTOR = '.oddit-section-collection-carousel__arrow--next';

  var sectionState = new Map();

  function getCardWidth(track) {
    var card = track.querySelector(CARD_SELECTOR);
    if (!card) return 0;
    var style = getComputedStyle(track);
    var gap = parseInt(style.columnGap, 10) || parseInt(style.gap, 10) || 0;
    return card.offsetWidth + gap;
  }

  function updateArrowStates(section, track) {
    var prevBtn = section.querySelector(PREV_SELECTOR);
    var nextBtn = section.querySelector(NEXT_SELECTOR);
    if (!prevBtn || !nextBtn) return;

    var scrollLeft = Math.round(track.scrollLeft);
    var maxScroll = track.scrollWidth - track.clientWidth;

    prevBtn.disabled = scrollLeft <= 1;
    nextBtn.disabled = scrollLeft >= maxScroll - 1;
  }

  function scrollToDirection(track, direction) {
    var cardWidth = getCardWidth(track);
    if (cardWidth === 0) return;
    track.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  }

  function handleAddToCart(e, killAutoplay) {
    var btn = e.target.closest('[data-add-to-cart]');
    if (!btn || !window.OdditCartDrawer) return;

    var variantId = Number(btn.getAttribute('data-variant-id'));
    if (!variantId) return;

    var quantity = Number(btn.getAttribute('data-quantity')) || 1;

    killAutoplay();

    // Store original text and show spinner
    var originalText = btn.textContent;
    btn.disabled = true;
    btn.classList.add('oddit-product-card__add-btn--loading');
    btn.textContent = '';

    window.OdditCartDrawer.addToCart(variantId, quantity)
      .then(function () {
        window.OdditCartDrawer.open();
      })
      .finally(function () {
        btn.disabled = false;
        btn.classList.remove('oddit-product-card__add-btn--loading');
        btn.textContent = originalText;
      });
  }

  function initSection(section) {
    var track = section.querySelector(TRACK_SELECTOR);
    if (!track) return;

    var prevBtn = section.querySelector(PREV_SELECTOR);
    var nextBtn = section.querySelector(NEXT_SELECTOR);

    function killAutoplay() {
      autoplayEnabled = false;
      stopAutoplay();
    }

    function onPrev() {
      killAutoplay();
      scrollToDirection(track, -1);
    }

    function onNext() {
      killAutoplay();
      scrollToDirection(track, 1);
    }

    function onScroll() {
      updateArrowStates(section, track);
    }

    function onTouchStart() {
      killAutoplay();
    }

    if (prevBtn) prevBtn.addEventListener('click', onPrev);
    if (nextBtn) nextBtn.addEventListener('click', onNext);
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('touchstart', onTouchStart, { passive: true });

    // Drag-to-scroll (desktop, mouse only). Click-suppression prevents the
    // mouseup from firing card links/add-to-cart when the user actually dragged.
    var dragState = { active: false, moved: false, startX: 0, startScrollLeft: 0 };
    var DRAG_THRESHOLD = 5;

    function onMouseDown(e) {
      if (e.button !== 0) return;
      // Allow drag from anywhere on a card. Only ignore explicit form
      // controls (add-to-cart button) so their clicks aren't hijacked.
      if (e.target.closest('button, [data-add-to-cart], input, select, textarea')) return;
      dragState.active = true;
      dragState.moved = false;
      dragState.startX = e.clientX;
      dragState.startScrollLeft = track.scrollLeft;
    }

    function onDragStart(e) {
      // Suppress the browser's native drag-and-drop of anchors/images so it
      // doesn't fight our pan. Plain clicks are unaffected.
      e.preventDefault();
    }

    function onMouseMove(e) {
      if (!dragState.active) return;
      var dx = e.clientX - dragState.startX;
      if (!dragState.moved && Math.abs(dx) > DRAG_THRESHOLD) {
        dragState.moved = true;
        killAutoplay();
        track.classList.add('is-dragging');
      }
      if (dragState.moved) {
        e.preventDefault();
        track.scrollLeft = dragState.startScrollLeft - dx;
      }
    }

    function endDrag() {
      if (!dragState.active) return;
      dragState.active = false;
      track.classList.remove('is-dragging');
    }

    function onMouseUp() {
      endDrag();
    }

    function onClickCapture(e) {
      if (dragState.moved) {
        e.preventDefault();
        e.stopPropagation();
        dragState.moved = false;
      }
    }

    track.addEventListener('mousedown', onMouseDown);
    track.addEventListener('dragstart', onDragStart);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    track.addEventListener('click', onClickCapture, true);

    // Add-to-cart delegation
    function onAddToCart(e) {
      handleAddToCart(e, killAutoplay);
    }
    section.addEventListener('click', onAddToCart);

    // Initial arrow state
    updateArrowStates(section, track);

    // Update arrows on resize
    var ro = new ResizeObserver(function () {
      updateArrowStates(section, track);
    });
    ro.observe(track);

    // Autoplay
    var autoplayEnabled = section.getAttribute('data-autoplay') === 'true';
    var autoplaySpeed = parseInt(section.getAttribute('data-autoplay-speed'), 10) || 5;
    var autoplayInterval = null;

    function startAutoplay() {
      if (!autoplayEnabled) return;
      stopAutoplay();
      autoplayInterval = setInterval(function () {
        var maxScroll = track.scrollWidth - track.clientWidth;
        if (Math.round(track.scrollLeft) >= maxScroll - 1) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollToDirection(track, 1);
        }
      }, autoplaySpeed * 1000);
    }

    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    }

    function onMouseEnter() { stopAutoplay(); }
    function onMouseLeave() { startAutoplay(); }
    function onFocusIn() { stopAutoplay(); }
    function onFocusOut() { startAutoplay(); }

    section.addEventListener('mouseenter', onMouseEnter);
    section.addEventListener('mouseleave', onMouseLeave);
    section.addEventListener('focusin', onFocusIn);
    section.addEventListener('focusout', onFocusOut);

    startAutoplay();

    sectionState.set(section, {
      prevBtn: prevBtn,
      nextBtn: nextBtn,
      track: track,
      onPrev: onPrev,
      onNext: onNext,
      onScroll: onScroll,
      onTouchStart: onTouchStart,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onFocusIn: onFocusIn,
      onFocusOut: onFocusOut,
      onAddToCart: onAddToCart,
      onMouseDown: onMouseDown,
      onDragStart: onDragStart,
      onMouseMove: onMouseMove,
      onMouseUp: onMouseUp,
      onClickCapture: onClickCapture,
      stopAutoplay: stopAutoplay,
      ro: ro
    });
  }

  function destroySection(section) {
    var state = sectionState.get(section);
    if (!state) return;

    if (state.prevBtn) state.prevBtn.removeEventListener('click', state.onPrev);
    if (state.nextBtn) state.nextBtn.removeEventListener('click', state.onNext);
    if (state.track) state.track.removeEventListener('scroll', state.onScroll);
    if (state.track) state.track.removeEventListener('touchstart', state.onTouchStart);
    if (state.track) state.track.removeEventListener('mousedown', state.onMouseDown);
    if (state.track) state.track.removeEventListener('dragstart', state.onDragStart);
    if (state.track) state.track.removeEventListener('click', state.onClickCapture, true);
    window.removeEventListener('mousemove', state.onMouseMove);
    window.removeEventListener('mouseup', state.onMouseUp);
    if (state.ro) state.ro.disconnect();
    if (state.stopAutoplay) state.stopAutoplay();
    section.removeEventListener('click', state.onAddToCart);
    section.removeEventListener('mouseenter', state.onMouseEnter);
    section.removeEventListener('mouseleave', state.onMouseLeave);
    section.removeEventListener('focusin', state.onFocusIn);
    section.removeEventListener('focusout', state.onFocusOut);

    sectionState.delete(section);
  }

  // Init all instances on page
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();